import AnalyticsProvider from '@root/core/src/contexts/analytics/analytics-provider';
import AuthController from '@root/agents.joinroot.com/src/components/auth-controller';
import AuthService from '@root/core/src/services/auth-service';
import ErrorBoundary from '@root/core/src/components/error-boundary';
import FormProvider from '@root/core/src/contexts/form/form-provider';
import PropTypes from '@root/vendor/prop-types';
import ProtectedApp from '@root/agents.joinroot.com/src/components/protected-app';
import PublicRouter from '@root/agents.joinroot.com/src/components/routers/public-router';
import SceneLoader from '@root/core/src/components/scene-loader';
import environment from '@root/core/src/utils/environment';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { AgentProvider } from '@root/agents.joinroot.com/src/contexts/agent-context';
import { PRODUCT_CHOICES, ProductChoiceProvider } from '@root/agents.joinroot.com/src/contexts/product-choice-context';
import { Suspense } from '@root/vendor/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

const SENSITIVE_QUERY_PARAMS = ['retry', 'auth'];

export function scrubSensitiveQueryParams(url) {
  if (!url) { return url; }

  try {
    const parsedUrl = new URL(url);
    SENSITIVE_QUERY_PARAMS.forEach((param) => parsedUrl.searchParams.delete(param));
    return parsedUrl.toString();
  } catch (error) {
    return url;
  }
}

function scrubDatadogEventUrls(event) {
  if (event.view?.url) { event.view.url = scrubSensitiveQueryParams(event.view.url); }
  if (event.view?.referrer) { event.view.referrer = scrubSensitiveQueryParams(event.view.referrer); }
  if (event.resource?.url) { event.resource.url = scrubSensitiveQueryParams(event.resource.url); }
  if (event.error?.resource?.url) { event.error.resource.url = scrubSensitiveQueryParams(event.error.resource.url); }
  return true;
}

function scrubDatadogLogUrls(log) {
  if (log.view?.url) { log.view.url = scrubSensitiveQueryParams(log.view.url); }
  if (log.view?.referrer) { log.view.referrer = scrubSensitiveQueryParams(log.view.referrer); }
  if (log.http?.url) { log.http.url = scrubSensitiveQueryParams(log.http.url); }
  if (log.url) { log.url = scrubSensitiveQueryParams(log.url); }
  return true;
}

// Detect if we should suppress DD RUM initialization
function shouldSuppressDdRum() {
  // Check if we're on the authenticate page
  // eslint-disable-next-line root/prevent-use-of-window-location
  const isAuthenticatePath = window.location.pathname.replace(/\/$/, '') === '/authenticate';

  // Check for quote_id parameter (indicates HostedX redirect)
  // eslint-disable-next-line root/prevent-use-of-window-location
  const urlParams = new URLSearchParams(window.location.search);
  const hasQuoteId = urlParams.get('quote_id') !== null;

  // Check if user is already authenticated
  const isAuthenticated = AuthService.getAccessToken() !== null;

  // Scenario 1: Auth interstitial for HostedX
  // We're on /authenticate with a quote_id but not authenticated
  const isAuthInterstitial = isAuthenticatePath && hasQuoteId && !isAuthenticated;

  // Scenario 2: Just bridged from portal to HostedX
  // Check sessionStorage flag set before redirect
  const isBridgeRedirect = sessionStorage.getItem('hostedx_bridge_in_progress') === 'true';

  // Clean up the flag if present
  if (isBridgeRedirect) {
    sessionStorage.removeItem('hostedx_bridge_in_progress');
  }

  return isAuthInterstitial || isBridgeRedirect;
}

const shouldInitDdRum = environment.dataDogEnabled && !shouldSuppressDdRum();

if (shouldInitDdRum) {
  datadogRum.init({
    applicationId: environment.dataDogApplicationId,
    clientToken: environment.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agents.joinroot.com',
    env: environment.environment,
    version: environment.releaseSha,
    enableExperimentalFeatures: ['feature_flags'],
    sessionSampleRate: 100,
    sessionReplaySampleRate: environment.dataDogSessionReplaySampleRate,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    compressIntakeRequests: true,
    allowedTracingUrls: [
      /https?:\/\/localhost(:\d+)?/,
      /https?:\/\/(.+\.)?joinroot.com(:\d+)?/,
      /https?:\/\/(.+\.)?root.com(:\d+)?/,
    ],
    trackSessionAcrossSubdomains: true,
    beforeSend: scrubDatadogEventUrls,
  });

  datadogLogs.init({
    clientToken: environment.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agents.joinroot.com',
    env: environment.environment,
    version: environment.releaseSha,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    sessionSampleRate: 100,
    beforeSend: scrubDatadogLogUrls,
  });

  datadogRum.startSessionReplayRecording();
}

function App({ routerComponent: Router }) {
  useAnalytics('APP_ENTRY');
  const productChoice = JSON.parse(sessionStorage.getItem('productChoice')) || PRODUCT_CHOICES.DAY_ZERO;

  return (
    <Router>
      <Suspense fallback={<SceneLoader hideHeader={true} />}>
        <AgentProvider>
          <ProductChoiceProvider value={productChoice}>
            <AuthController
              ProtectedApp={ProtectedApp}
              PublicRouter={PublicRouter}
            />
          </ProductChoiceProvider>
        </AgentProvider>
      </Suspense>
    </Router>
  );
}

App.propTypes = {
  routerComponent: PropTypes.func.isRequired,
};

export default function AppEntry({ routerComponent }) {
  return (
    <ErrorBoundary
      errorTextSuffix={'Please share this screenshot with us, and some information on how you ran into this error.'}
      showHeader={false}
      showTimestamp={true}
    >
      <FormProvider>
        <AnalyticsProvider eventPrefix={'ROOT_AGENTS'}>
          <App routerComponent={routerComponent} />
        </AnalyticsProvider>
      </FormProvider>
    </ErrorBoundary>
  );
}

AppEntry.propTypes = {
  routerComponent: PropTypes.func.isRequired,
  routerProps: PropTypes.object,
};

const ROOT_AGENTS_APP_DOMAIN = 'agents.joinroot.com';
export { ROOT_AGENTS_APP_DOMAIN };
